import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LEAD } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CheckCircle2, Lock, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COVERAGE_OPTIONS = [
  "$50,000 – $100,000",
  "$100,000 – $250,000",
  "$250,000 – $500,000",
  "$500,000 – $1,000,000",
  "$1,000,000+",
  "Not sure — help me decide",
];

const PLAN_OPTIONS = [
  { value: "term-life", label: "Term Life" },
  { value: "whole-life", label: "Whole Life" },
  { value: "iul", label: "Indexed Universal Life" },
  { value: "final-expense", label: "Final Expense" },
  { value: "mortgage-protection", label: "Mortgage Protection" },
  { value: "not-sure", label: "Not sure — recommend one" },
];

export default function LeadForm() {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", age: "",
    coverage_amount: "", tobacco_use: "no",
    plan_interest: "not-sure", message: "",
  });

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, plan_interest: e.detail }));
    window.addEventListener("prefill-plan", handler);
    return () => window.removeEventListener("prefill-plan", handler);
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.phone || !form.age || !form.coverage_amount) {
      toast.error("Please fill in all required fields.");
      return;
    }
    const ageNum = parseInt(form.age, 10);
    if (Number.isNaN(ageNum) || ageNum < 18 || ageNum > 85) {
      toast.error("Age must be between 18 and 85.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, age: ageNum });
      setSuccess(true);
      toast.success("Got it! A licensed agent will reach out within 1 business day.");
      setForm({ name: "", email: "", phone: "", age: "", coverage_amount: "", tobacco_use: "no", plan_interest: "not-sure", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Unable to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid={LEAD.section}
      className="py-16 sm:py-24 lg:py-32 bg-primary text-primary-foreground relative overflow-hidden"
    >
      <div className="grain absolute inset-0 opacity-50 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <p className="overline !text-accent mb-4">Free, no-obligation quote</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6">
            Tell us about<br />
            <em className="not-italic text-accent">your people.</em>
          </h2>
          <p className="text-primary-foreground/75 leading-relaxed mb-10 max-w-md">
            Sixty seconds. No medical exam. No credit pull. A licensed agent will email or call
            within one business day with your best options.
          </p>

          <ul className="space-y-4 text-sm">
            <li className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-accent mt-0.5" strokeWidth={1.5} /> Side-by-side quotes from 25+ A-rated carriers</li>
            <li className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-accent mt-0.5" strokeWidth={1.5} /> No agent fees, ever — carriers pay us, not you</li>
            <li className="flex items-start gap-3"><Lock className="h-5 w-5 text-accent mt-0.5" strokeWidth={1.5} /> Bank-grade encryption. We never sell your information.</li>
          </ul>
        </div>

        <div className="lg:col-span-7">
          <form
            data-testid={LEAD.form}
            onSubmit={onSubmit}
            className="rounded-[1.5rem] sm:rounded-[2rem] bg-background text-foreground p-6 sm:p-8 lg:p-10 soft-shadow"
          >
            {success ? (
              <div data-testid={LEAD.success} className="py-16 text-center">
                <div className="mx-auto h-14 w-14 rounded-full bg-accent/15 flex items-center justify-center mb-6">
                  <Sparkles className="h-6 w-6 text-accent" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-3xl text-primary mb-3">Thank you.</h3>
                <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
                  A licensed Faith Family Finance agent will reach out within one business day with
                  your personalized quote.
                </p>
                <Button
                  type="button"
                  onClick={() => setSuccess(false)}
                  variant="outline"
                  className="rounded-full mt-8 bg-transparent border-border"
                >
                  Submit another quote
                </Button>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full name" htmlFor={LEAD.name}>
                    <Input id={LEAD.name} data-testid={LEAD.name} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Sarah Johnson" className="h-12 rounded-xl" required />
                  </Field>
                  <Field label="Email" htmlFor={LEAD.email}>
                    <Input id={LEAD.email} data-testid={LEAD.email} type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="sarah@email.com" className="h-12 rounded-xl" required />
                  </Field>
                  <Field label="Phone" htmlFor={LEAD.phone}>
                    <Input id={LEAD.phone} data-testid={LEAD.phone} value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(555) 123-4567" className="h-12 rounded-xl" required />
                  </Field>
                  <Field label="Age" htmlFor={LEAD.age}>
                    <Input id={LEAD.age} data-testid={LEAD.age} type="number" min={18} max={85} value={form.age} onChange={(e) => update("age", e.target.value)} placeholder="38" className="h-12 rounded-xl" required />
                  </Field>
                </div>

                <div className="grid sm:grid-cols-2 gap-5 mt-5">
                  <Field label="Coverage amount" htmlFor={LEAD.coverage}>
                    <Select value={form.coverage_amount} onValueChange={(v) => update("coverage_amount", v)}>
                      <SelectTrigger id={LEAD.coverage} data-testid={LEAD.coverage} className="h-12 rounded-xl">
                        <SelectValue placeholder="Choose coverage" />
                      </SelectTrigger>
                      <SelectContent>
                        {COVERAGE_OPTIONS.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Plan of interest" htmlFor={LEAD.plan}>
                    <Select value={form.plan_interest} onValueChange={(v) => update("plan_interest", v)}>
                      <SelectTrigger id={LEAD.plan} data-testid={LEAD.plan} className="h-12 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PLAN_OPTIONS.map((p) => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="mt-5">
                  <Label className="text-sm font-medium text-foreground">Tobacco use in the last 12 months?</Label>
                  <RadioGroup
                    value={form.tobacco_use}
                    onValueChange={(v) => update("tobacco_use", v)}
                    className="flex gap-6 mt-3"
                    data-testid={LEAD.tobacco}
                  >
                    <label className="flex items-center gap-2 cursor-pointer">
                      <RadioGroupItem value="no" id="tobacco-no" />
                      <span className="text-sm">No</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <RadioGroupItem value="yes" id="tobacco-yes" />
                      <span className="text-sm">Yes</span>
                    </label>
                  </RadioGroup>
                </div>

                <Field label="Anything we should know? (optional)" htmlFor={LEAD.message} className="mt-5">
                  <Textarea
                    id={LEAD.message}
                    data-testid={LEAD.message}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Best time to reach you, family situation, etc."
                    className="min-h-[110px] rounded-xl"
                  />
                </Field>

                <Button
                  type="submit"
                  data-testid={LEAD.submit}
                  disabled={submitting}
                  className="mt-8 w-full h-13 py-3 rounded-full bg-primary hover:bg-primary/90 text-base"
                >
                  {submitting ? "Sending…" : "Get my free quote"}
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-4">
                  By submitting, you agree to be contacted by a licensed Faith Family Finance agent.
                </p>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children, className = "" }) {
  return (
    <div className={className}>
      <Label htmlFor={htmlFor} className="text-sm font-medium text-foreground">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
